'use client'

import debounce from 'lodash.debounce'
import { useCallback, useEffect } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'

import {
  CmsSellerFilters,
  CUSTOM_CMS_SELLER_TYPE_MESSIKA_STORE,
  CUSTOM_CMS_SELLER_TYPE_RESELLERS,
} from '../../../../domain/seller'
import { useTranslation } from '../../../context/ConfigContext'
import { Button } from '../../Button/Button'
import InputCheckbox from '../../Form/InputCheckbox'
import CustomSelect from '../../Form/InputWrapper/CustomSelect'
import { RADIUS_TYPE_KM } from '../constants'
import InputRadiusCity from '../Form/InputRadiusCity'

import GoogleMapAutoCompleteCity from './GoogleMapAutoCompleteCity'
import { googleTransformFormDataToCmsSellerFilter } from './helpers'
import { GoogleFormData } from './types'
import useStoreCategories from '../../../../core/hook/useStoreCategories'

type GoogleMapSellersFiltersMapProps = {
  hasCitySearch: boolean
  onSubmitFilterForm: (value: CmsSellerFilters) => void
  centerMapFromLatLng?: (lat: number, lng: number) => void
  getPlaceFromPlaceId?: (placeId: string) => Promise<google.maps.places.PlaceResult | null>
}

const GoogleMapSellersFiltersMap = ({
  centerMapFromLatLng,
  getPlaceFromPlaceId,
  hasCitySearch,
  onSubmitFilterForm,
}: GoogleMapSellersFiltersMapProps) => {
  const t = useTranslation()
  const { handleSubmit, register, setValue, control, watch, reset } = useForm<GoogleFormData>({
    defaultValues: {
      radius: 0,
      typeRadius: RADIUS_TYPE_KM,
      type: 'all',
    },
  })
  const storeCategories = useStoreCategories()

  const typeRadiusValue = watch('typeRadius')

  const onSubmitForm: SubmitHandler<GoogleFormData> = useCallback(
    (values) => {
      const cmsSellerFilters = googleTransformFormDataToCmsSellerFilter(values)
      onSubmitFilterForm(cmsSellerFilters)
    },
    [onSubmitFilterForm],
  )

  const onSubmitFormDebounce = useCallback(
    debounce((data: GoogleFormData) => {
      onSubmitForm(data)
    }, 500),
    [onSubmitForm],
  )

  const onChangePlace = useCallback(
    (place: google.maps.places.PlaceResult) => {
      setValue('place', place, { shouldValidate: true })
      if (place.geometry?.location) {
        centerMapFromLatLng?.(place.geometry.location.lat(), place.geometry.location.lng())
      }
    },
    [centerMapFromLatLng, setValue],
  )

  // handle submit search on change form
  useEffect(() => {
    watch((data) => onSubmitFormDebounce(data))
  }, [watch, onSubmitFormDebounce])

  return (
    <form onSubmit={handleSubmit(onSubmitForm)} className='sellers-filters-map'>
      {hasCitySearch && getPlaceFromPlaceId && (
        <>
          <GoogleMapAutoCompleteCity
            onChangePlace={onChangePlace}
            getPlaceFromPlaceId={getPlaceFromPlaceId}
          />
          <Controller
            name='radius'
            control={control}
            render={({ field }) => (
              <InputRadiusCity
                typeRadius={typeRadiusValue}
                {...field}
                onChange={(radius, typeRadius) => {
                  setValue('radius', radius, { shouldValidate: true })
                  setValue('typeRadius', typeRadius, { shouldValidate: true })
                }}
              />
            )}
          />
        </>
      )}

      <div className='sellers-filters-map-type'>
        <p className='sellers-filters-map-type-title'>{t('type of store')}</p>
        <Controller
          name='type'
          control={control}
          render={({ field }) => (
            <CustomSelect
              placeholder=''
              selectedValue={field.value}
              onChange={field.onChange}
              list={[
                {
                  title: t('All', {}, false),
                  value: 'all',
                  id: 'all',
                },
                {
                  title: t('Messika Boutiques', {}, false),
                  value: CUSTOM_CMS_SELLER_TYPE_MESSIKA_STORE,
                  id: CUSTOM_CMS_SELLER_TYPE_MESSIKA_STORE,
                },
                {
                  title: t('Revendeurs agréés', {}, false),
                  value: CUSTOM_CMS_SELLER_TYPE_RESELLERS,
                  id: CUSTOM_CMS_SELLER_TYPE_RESELLERS,
                },
              ]}
            />
          )}
        />
      </div>

      <div className='sellers-filters-map-category'>
        <p className='sellers-filters-map-category-title'>
          {t('Catégories de Produits')}
        </p>
        <div className='sellers-filters-map-category-list'>
          {storeCategories?.data?.getCategoryValues?.value?.split(',').map((storeCategory) => (
            <InputCheckbox
              key={storeCategory}
              {...register('categories')}
              label={storeCategory}
              value={storeCategory}
              showBorderCheckbox
            />
          ))}
        </div>
      </div>

      <div className='sellers-filters-map-buttons'>
        <Button
          label={t('Reset', {}, false)}
          onClick={() => {
            reset()
            onSubmitFilterForm({})
          }}
        />
      </div>
    </form>
  )
}

export default GoogleMapSellersFiltersMap
